import * as React from 'react';
import validator from 'validator';
import {Note} from '@packages/systems/spring';
import {styled} from '@packages/utilities/styled';
import {Plugins, TypeImplementation} from '@packages/systems/wfdl';
import {
  EHole,
  EUnion,
  ERecord,
  ELiteral,
  TTypeApplication,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import TextInput from '@packages/systems/design-system/TextInput';
import {isVideoURL} from '@packages/systems/core/utils/FigureUtils';
import {cleanHref} from '@packages/systems/core/utils/HtmlUtils';
import {isValidLink} from '@packages/utilities/form-validation';
import {
  ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';

type names = 'Email' | 'Link' | 'Video';

const StyledNote = styled(Note)(({theme: {gutters}}) => ({
  marginTop: gutters.x4,
}));

const typeNameMap = {
  Email: {
    lowercase: 'email',
    validator: validator.isEmail,
    qualifiedName: qualify(Plugins.name('Email'), typeName('Email')),
  },
  Link: {
    lowercase: 'link',
    validator: isValidLink,
    qualifiedName: qualify(Plugins.name('Link'), typeName('Link')),
  },
  Video: {
    lowercase: 'video link',
    validator: isVideoURL,
    qualifiedName: qualify(Plugins.name('Video'), typeName('Video')),
  },
} as const;

const makeLiteral = (tyName: string, value: string) =>
  ELiteral({
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly Email: { readonly lowercase: "email"; readonly validator: (str: string, options?: IsEmailOptions | undefined) => boolean; readonly qualifiedName: Qualified<string>; }; readonly Link: { ...; }; readonly Video: { ...; }; }'.
    name: typeNameMap[tyName].qualifiedName,
    value,
  });

const getLiteral = (expr: any) => {
  const literal = ContextualExpressions.getLiteral(stripAnn(expr));
  return literal && typeof literal.value === 'string' ? literal.value : '';
};

const isValidInput = (tyName: string | names, input: string) =>
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly Email: { readonly lowercase: "email"; readonly validator: (str: string, options?: IsEmailOptions | undefined) => boolean; readonly qualifiedName: Qualified<string>; }; readonly Link: { ...; }; readonly Video: { ...; }; }'.
  input === '' || typeNameMap[tyName].validator(input);

function makeRender(
  tyName: string | names,
  getValue: (expr: ContextualExpression) => string,
  getAction: (val: string) => TypeImplementation.ExpressionEditorAction | null
): React.ComponentType<{
  expr: ContextualExpression;
  dispatch: (action: TypeImplementation.ExpressionEditorAction) => void;
}> {
  return function DataType({expr, dispatch}) {
    const dispatchRef = React.useRef(dispatch);
    dispatchRef.current = dispatch;
    const val = getValue(expr);
    const [value, setValue] = React.useState(val);
    const valid = isValidInput(tyName, value);

    // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
    const onChange = React.useCallback((e) => {
      const inputVal = e.target.value;
      setValue(inputVal);
      const action = getAction(inputVal);
      if (action) dispatchRef.current(action);
    }, []);

    const onBlur = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
      (e) => {
        const inputVal =
          tyName === 'Link'
            ? cleanHref(e.currentTarget.value)
            : e.currentTarget.value;
        const action = getAction(inputVal);
        if (action) {
          setValue(inputVal);
          dispatchRef.current(action);
        } else {
          // If we blur the field but no action was dispatched revert to the
          // original `val` to match what is stored in the expression
          setValue(val);
        }
      },
      [val]
    );

    return (
      <>
        <TextInput
          onChange={onChange}
          onBlur={onBlur}
          value={value}
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly Email: { readonly lowercase: "email"; readonly validator: (str: string, options?: IsEmailOptions | undefined) => boolean; readonly qualifiedName: Qualified<string>; }; readonly Link: { ...; }; readonly Video: { ...; }; }'.
          placeholder={`Enter ${typeNameMap[tyName].lowercase}...`}
        />
        {!valid ? (
          <StyledNote hasIcon={false} colorVariant="danger">
            {/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly Email: { readonly lowercase: "email"; readonly validator: (str: string, options?: IsEmailOptions | undefined) => boolean; readonly qualifiedName: Qualified<string>; }; readonly Link: { ...; }; readonly Video: { ...; }; }'. */}
            {`Should be a valid ${typeNameMap[tyName].lowercase}${
              tyName === 'Link' ? ' like "http://webflow.com"' : ''
            }.`}
          </StyledNote>
        ) : null}
      </>
    );
  };
}

export const makeRenderFunction = (tyName: names) =>
  makeRender(tyName, getLiteral, (newVal) => {
    if (newVal === '') return updateExpressionAction(EHole());
    return isValidInput(tyName, newVal)
      ? updateExpressionAction(makeLiteral(tyName, newVal))
      : null;
  });

const tyOption = TTypeConstructor(
  qualify(Plugins.name('Option'), typeName('Option'))
);

export const makeOptionalAlias = (tyName: names) => {
  const OptionInput = makeRender(
    tyName,
    (expr) => {
      const union = ContextualExpressions.getUnion(stripAnn(expr));
      return union && union.tag === 'Some' ? getLiteral(union.value) : '';
    },
    (newVal) => {
      if (newVal === '')
        return updateExpressionAction(
          EUnion({
            tag: 'None',
            value: ERecord({}),
          })
        );

      if (isValidInput(tyName, newVal))
        return updateExpressionAction(
          EUnion({tag: 'Some', value: makeLiteral(tyName, newVal)})
        );

      return null;
    }
  );

  return TypeImplementation.Alias({
    type: TTypeApplication({
      con: tyOption,
      arg: TTypeConstructor(typeNameMap[tyName].qualifiedName),
    }),
    render: ({expr, dispatch}) =>
      ContextualExpressions.isBinding(stripAnn(expr)) ? (
        <ExpressionEditor expression={expr} dispatch={dispatch} />
      ) : (
        <OptionInput expr={expr} dispatch={dispatch} />
      ),
  });
};
